import { createSelector } from "@ngrx/store";
import { selectQueryParam } from "src/app/router.selector";
import { DataSourceState } from "../../data-source/state/data-source.reducer";
import { selectDataSourceState } from "../../data-source/state/data-source.selectors";
import {
  CommonModelType,
  ModelState,
  MODEL_TYPE_TO_COMMON_TYPE,
} from "../../models/state/model";
import { selectModelState } from "../../models/state/model.selectors";
import { ContextKey, ContextParams, ModelContext } from "./context.model";

export const selectDataSourceContext = createSelector(
  selectDataSourceState,
  selectQueryParam(ContextKey.DataSourceContextKey),
  (dataSourceState: DataSourceState, dataSourceID: string) => {
    if (!dataSourceID || !dataSourceState) return null;
    return dataSourceState.entities
      ? dataSourceState.entities[dataSourceID]
      : null;
  },
);

export const selectCategoryModelContext = createSelector(
  selectModelState,
  selectQueryParam(ContextKey.Focused),
  selectQueryParam(ContextKey.CategoryContextKey),
  (modelState: ModelState, focused: string, modelVersionID: string) =>
    getModelAndVersion(
      modelState,
      focused,
      modelVersionID,
      CommonModelType.CATEGORY,
    ),
);

export const selectSentimentModelContext = createSelector(
  selectModelState,
  selectQueryParam(ContextKey.Focused),
  selectQueryParam(ContextKey.SentimentContextKey),
  (modelState: ModelState, focused: string, modelVersionID: string) =>
    getModelAndVersion(
      modelState,
      focused,
      modelVersionID,
      CommonModelType.SENTIMENT,
    ),
);

export const selectTopicModelContext = createSelector(
  selectModelState,
  selectQueryParam(ContextKey.Focused),
  selectQueryParam(ContextKey.TopicContextKey),
  (modelState: ModelState, focused: string, modelVersionID: string) =>
    getModelAndVersion(
      modelState,
      focused,
      modelVersionID,
      CommonModelType.TOPIC,
    ),
);

export const selectEmotionModelContext = createSelector(
  selectModelState,
  selectQueryParam(ContextKey.Focused),
  selectQueryParam(ContextKey.EmotionContextKey),
  (modelState: ModelState, focused: string, modelVersionID: string) =>
    getModelAndVersion(
      modelState,
      focused,
      modelVersionID,
      CommonModelType.EMOTION,
    ),
);

export const selectIntentModelContext = createSelector(
  selectModelState,
  selectQueryParam(ContextKey.Focused),
  selectQueryParam(ContextKey.IntentContextKey),
  (modelState: ModelState, focused: string, modelVersionID: string) =>
    getModelAndVersion(
      modelState,
      focused,
      modelVersionID,
      CommonModelType.INTENT,
    ),
);

function getModelAndVersion(
  modelState: ModelState,
  focused: string,
  modelVersionID: string,
  modelType: CommonModelType,
): ModelContext {
  if (!focused) return null;

  if (
    !modelState ||
    !modelState.summaries?.length ||
    !modelState.versions?.length
  )
    return null;
  if (!modelVersionID) return getRandomModelAndVersion(modelState, modelType);

  const version = modelState?.versions?.find((c) => c.uuid === modelVersionID);
  const model = modelState?.summaries?.find(
    (m) => m.uuid === version?.model_id,
  );

  if (version && model) {
    return {
      model,
      version,
      readOnly: !!version.release_context,
    };
  }
  return null;
}

export const selectAllContextParams = createSelector(
  selectQueryParam(ContextKey.DataSourceContextKey),
  selectQueryParam(ContextKey.CategoryContextKey),
  selectQueryParam(ContextKey.SentimentContextKey),
  selectQueryParam(ContextKey.TopicContextKey),
  selectQueryParam(ContextKey.EmotionContextKey),
  selectQueryParam(ContextKey.IntentContextKey),
  (
    ds: string | undefined,
    c: string | undefined,
    s: string | undefined,
    t: string | undefined,
    e: string | undefined,
    i: string | undefined,
  ): ContextParams => ({
    [ContextKey.DataSourceContextKey]: ds,
    [ContextKey.CategoryContextKey]: c,
    [ContextKey.SentimentContextKey]: s,
    [ContextKey.TopicContextKey]: t,
    [ContextKey.EmotionContextKey]: e,
    [ContextKey.IntentContextKey]: i,
  }),
);

function getRandomModelAndVersion(
  modelState: ModelState,
  modelType: CommonModelType,
): ModelContext {
  // Just select any model with the appropriate model type
  // This fixes the bug user gets when choosing a model on import and then deleting that model and then goes to refresh the category
  const model = modelState?.summaries?.find(
    (m) => MODEL_TYPE_TO_COMMON_TYPE[m?.model_type] === modelType,
  );
  const version = modelState?.versions?.find(
    (v) => v?.model_id === model?.uuid,
  );

  if (version && model) {
    return {
      model,
      version,
      readOnly: !!version.release_context,
    };
  }
  return null;
}
